import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Sparkles, ChevronRight } from 'lucide-react';
import { useTelegram } from '@/hooks/useTelegram';

/**
 * Практика под то, что человек ТОЛЬКО ЧТО отметил.
 *
 * ЗАЧЕМ (ревизия Релакса 04.09.2026). Раздел мёртв: 15 медитаций слушают
 * 3 раза в месяц, дыхательных сессий за всё время — НОЛЬ. Причина не в
 * дизайне и не в нехватке контента: между «записать обед» и «лечь подышать
 * десять минут» нет мостика, а вкладка его не создаёт.
 *
 * При этом человек сам говорит, когда ему тяжело: ставит настроение 1-2
 * и теги «работа», «дедлайн», «ссора». И на это НЕ ПРОИСХОДИЛО НИЧЕГО —
 * в карточке отметки дня не было ни одного упоминания релакса.
 *
 * Здесь и есть тот момент. Не раздел, куда надо прийти, а одна строка
 * там, где человек только что сказал, что ему плохо.
 *
 * По хартии ненавязчивости: строка, а не окно; одна и не повторяется;
 * молчит, когда день прошёл нормально — предлагать дыхание тому,
 * у кого «отличный день», значит обесценить предложение.
 */

type Suggestion = {
  /** Куда ведём: медитация по slug или дыхание по технике */
  href: string;
  title: string;
  /** Почему именно это — человеку видно связь с его отметкой */
  reason: string;
  minutes: string;
};

export function pickPractice(
  mood: number | null | undefined,
  sleep: number | null | undefined,
  tags: string[] | null | undefined,
): Suggestion | null {
  const t = new Set(tags ?? []);

  // Сначала — самое острое. Выжатый день важнее любого тега.
  if (mood != null && mood <= 2) {
    // Дыхание, а не медитация: три минуты, без наушников, не надо ложиться.
    // Именно поэтому оно уместнее всего в момент «накрыло».
    return {
      href: '/breathing/physiological_sigh',
      title: 'Физиологический вздох',
      reason: 'День был тяжёлым — это снимает напряжение быстрее всего',
      minutes: '2 минуты',
    };
  }

  if (t.has('ссора')) {
    return {
      // Голосовые записи скрыты 13.10.2026 (миграция 066) — вместо них дыхание и музыка
      href: '/breathing/coherent',
      title: 'Когерентное дыхание',
      reason: 'После ссоры ровное дыхание быстрее всего возвращает опору',
      minutes: '3 минуты',
    };
  }

  if (t.has('одиноко')) {
    return {
      href: '/relax/player/forest-light',
      title: 'Лесной свет',
      reason: 'Ты отметил(а), что было одиноко — побудь немного в тёплом звуке',
      minutes: '6 минут',
    };
  }

  if (t.has('дедлайн') || t.has('работа')) {
    return {
      href: '/breathing/box',
      title: 'Бокс-дыхание',
      reason: 'После рабочего напряжения помогает переключиться',
      minutes: '3 минуты',
    };
  }

  if (t.has('болел(а)')) {
    return {
      href: '/relax/player/forest-light',
      title: 'Лесной свет',
      reason: 'Когда тело не в форме, лучше не нагружать, а восстановить',
      minutes: '6 минут',
    };
  }

  // Плохой сон — вечернее предложение на следующую ночь
  if (sleep != null && sleep <= 2) {
    return {
      href: '/breathing/4_7_8',
      title: 'Дыхание 4-7-8',
      reason: 'Прошлая ночь не задалась — попробуй перед этой: длинный выдох помогает уснуть',
      minutes: '2 минуты',
    };
  }

  // День прошёл нормально — молчим. Предлагать практику тому, у кого всё
  // хорошо, значит превратить заботу в фоновый шум.
  return null;
}

export function CheckinRelaxSuggestion({
  mood, sleep, tags,
}: {
  mood: number | null | undefined;
  sleep: number | null | undefined;
  tags: string[] | null | undefined;
}) {
  const nav = useNavigate();
  const { haptic } = useTelegram();
  const s = pickPractice(mood, sleep, tags);
  if (!s) return null;

  return (
    <motion.button
      initial={{ opacity: 0, y: -4 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.2 }}
      onClick={() => { haptic('light'); nav(s.href); }}
      className="w-full flex items-center gap-2.5 px-3 py-2.5 rounded-xl border text-left active:scale-[0.99] transition-transform"
      style={{ borderColor: 'rgba(61,216,255,0.3)', background: 'rgba(61,216,255,0.06)' }}
    >
      <div className="w-[26px] h-[26px] rounded-full bg-sky-glow/15 flex items-center justify-center shrink-0">
        <Sparkles size={14} className="text-sky-glow" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-[13px] font-medium leading-snug">
          {s.title} · {s.minutes}
        </div>
        <div className="text-[11px] text-ink-dim leading-snug">{s.reason}</div>
      </div>
      <ChevronRight size={15} className="text-ink-dim shrink-0" />
    </motion.button>
  );
}
